import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { User, Vehicle, Booking } from '@/types';
import type { Driver } from '@/services/driverService';

export interface SearchResultItem {
  id: string;
  type: 'user' | 'vehicle' | 'booking' | 'driver';
  title: string;
  subtitle: string;
  description?: string;
  path: string;
  data: User | Vehicle | Booking | Driver;
  matchedFields: string[];
}

interface SearchState {
  query: string;
  results: SearchResultItem[];
  recentSearches: string[];
  isSearching: boolean;
  isOpen: boolean;
  selectedIndex: number;
}

const initialState: SearchState = {
  query: '',
  results: [],
  recentSearches: JSON.parse(localStorage.getItem('recentSearches') || '[]'),
  isSearching: false,
  isOpen: false,
  selectedIndex: -1,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
      state.selectedIndex = -1;
      
      // Save to recent searches if query is meaningful
      if (action.payload.length > 2 && !state.recentSearches.includes(action.payload)) {
        state.recentSearches.unshift(action.payload);
        // Keep only last 10 searches
        if (state.recentSearches.length > 10) {
          state.recentSearches = state.recentSearches.slice(0, 10);
        }
        localStorage.setItem('recentSearches', JSON.stringify(state.recentSearches));
      }
    },
    
    setResults: (state, action: PayloadAction<SearchResultItem[]>) => {
      state.results = action.payload;
      state.selectedIndex = -1;
    },
    
    setIsSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
    },
    
    setIsOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
      if (!action.payload) {
        state.selectedIndex = -1;
      }
    },
    
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    
    clearResults: (state) => {
      state.results = [];
      state.query = '';
      state.selectedIndex = -1;
    },
    
    clearRecentSearches: (state) => {
      state.recentSearches = [];
      localStorage.removeItem('recentSearches');
    },
    
    removeRecentSearch: (state, action: PayloadAction<string>) => {
      state.recentSearches = state.recentSearches.filter(search => search !== action.payload);
      localStorage.setItem('recentSearches', JSON.stringify(state.recentSearches));
    },
  },
});

export const {
  setQuery,
  setResults,
  setIsSearching,
  setIsOpen,
  setSelectedIndex,
  clearResults,
  clearRecentSearches,
  removeRecentSearch,
} = searchSlice.actions;

export default searchSlice.reducer;

// Selectors
export const selectSearch = (state: { search: SearchState }) => state.search;
export const selectSearchQuery = (state: { search: SearchState }) => state.search.query;
export const selectSearchResults = (state: { search: SearchState }) => state.search.results;
export const selectRecentSearches = (state: { search: SearchState }) => state.search.recentSearches;
export const selectIsSearching = (state: { search: SearchState }) => state.search.isSearching;
export const selectIsSearchOpen = (state: { search: SearchState }) => state.search.isOpen;
export const selectSelectedIndex = (state: { search: SearchState }) => state.search.selectedIndex;